/* istanbul ignore file */
// TODO: Proper test coverage at some point?
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, AfterViewInit, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import JSONEditor from 'jsoneditor';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import get from 'lodash-es/get';
import omit from 'lodash-es/omit';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import replaceCircular from '@finder/shared/utils/object/replaceCircular';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';

@Component({
    selector: 'finder-json-editor',
    templateUrl: './json-editor.component.html',
    styleUrls: ['./json-editor.component.scss'],
})
export class JsonEditorComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: any;
    @Input() enableCalls = true;
    @Input() mode = 'view';
    @Input() saveEvent: Observable<void>;
    @Output() dataExported = new EventEmitter<any>();

    dataUrls: any;
    dataUrl: string;

    private jsonEditor: JSONEditor;
    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        @Inject(DOCUMENT) private document: Document,
        private window: WindowRef,
        private lazyLoader: LazyLoaderService,
    ) {}

    ngOnInit() {
        if (!this.saveEvent) {
            return;
        }
        // When a "save" is triggered from the parent, this will tell us we need
        // to emit the JSON editor data. If the data is malformed the `get()` call
        // will throw, so we wrap a try/catch and return info about any error.
        this.saveEvent
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                let result;
                try {
                    result = this.jsonEditor.get();
                } catch (e) {
                    result = {
                        hasError: true,
                        message: e
                    };
                }
                this.dataExported.emit(result);
            });
    }

    ngAfterViewInit() {
        this.setDataUrls();
        this.formatJson();
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setData(event) {
        // If we get the dataUrl item then we can just pluck the data.
        // For mobile width we use a select list and we cannot bind the
        // object (at least I could not get it to work) so instead we get
        // the dataUrl index and then get that object and grab the data prop.
        // To be clear: ReactiveFormModule or FormsModule `ngValue` does not
        // work as all the example state - `event.target.value` was _ALWAYS_
        // the detail property ¯\_(ツ)_/¯
        let data = event.data || this.dataUrls[event.target.value].data;

        // Set the URL
        this.dataUrl = event.url || this.dataUrls[event.target.value].url;

        // Check for vendo config data that is stringified - if we have a
        // `config` property only then we will also parse the result
        const keys = Object.keys(data);
        if (keys.length === 1 && keys[0] === 'config') {
            try {
                data = {
                    parsed: JSON.parse(data.config),
                    original: {
                        config: data.config,
                    },
                };
            } catch (e) {
                console.error(`Error parsing results`, data);
            }
        }

        this.jsonEditor.set(data);
    }

    private formatJson() {
        const editor = this.elementRef.nativeElement.querySelector('#json');
        this.jsonEditor = new JSONEditor(editor, { mode: this.mode }, {});

        if (!editor || !this.jsonEditor) {
            return;
        }

        // Attach CSS
        this.lazyLoader.lazyLoadCssResource('/advanced-finder/styles/debug-panel/jsoneditor.min.css', false);
        // Add dark theme
        this.lazyLoader.lazyLoadCssResource('/advanced-finder/styles/debug-panel/darktheme.css', false);
        // If calls are not enabled, set the JSON editor data
        if (this.enableCalls === false) {
            this.jsonEditor.set(this.data);
        }
    }

    private setDataUrls() {
        const dataUrls = get(this.data, 'debug.dataUrls', []);

        if (dataUrls.length) {
            // I tried using `cloneDeep` but there are some circular refs
            // that break the JSON editor, so this was the best solution.
            // Using JSON.parse and JSON.stringify effectively clones the
            // object and then we can pass the function into the stringify
            // call to eliminate the circular dependencies and it works!
            const processedData = JSON.parse(JSON.stringify(
                omit(this.data, ['debug']),
                replaceCircular()
            ));

            dataUrls.push({
                name: 'Processed Result',
                detail: 'Post-processed result of all calls.',
                url: this.window.nativeWindow.location.href,
                data: processedData,
            });
        }

        this.dataUrls = dataUrls;
    }
}
